import { useCallback } from "react";
import { ProductLevel } from "@atlas-int/data/types";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";
import { useTooltipDispatchRef } from "@atlas-int/viz/shared";
import type { TooltipDatum } from "@atlas-int/viz/shared";

export interface OnHoverParams {
  datum: EnrichedTradeDataItem | undefined;
  leaf?: unknown;
  setTooltipFixed?: boolean | undefined;
  setTooltipCoords?: [number, number] | undefined;
  fillColor?: string | undefined;
}

/**
 * Treemap hover/fixed tooltip dispatch. Must be used under {@link TooltipStateIsolationProvider}
 * so handlers call into `useTooltipState` via a stable ref (chart memoization stays effective).
 */
export const useTooltipHandler = (
  setHighlightedItem: (item: string | undefined) => void,
) => {
  const dispatchRef = useTooltipDispatchRef();

  const onHover = useCallback(
    ({
      datum,
      leaf: _leaf,
      setTooltipFixed,
      setTooltipCoords,
      fillColor,
    }: OnHoverParams) => {
      if (datum) {
        if (setTooltipFixed) {
          const itemId =
            datum.productId ??
            (typeof datum.id === "string"
              ? datum.id
              : datum.id != null
                ? String(datum.id)
                : undefined);
          setHighlightedItem(itemId);
        }

        const { productLevel } = datum;
        const kind = datum.productId ? ("product" as const) : ("location" as const);

        let contentData: Record<string, unknown>;
        if (productLevel && productLevel === ProductLevel.ProductSection) {
          contentData = {
            ...datum,
            kind,
            topLevelParent: datum.productId,
          };
        } else {
          contentData = { ...datum, kind };
        }

        if (setTooltipFixed && setTooltipCoords) {
          dispatchRef.current.fixTooltip(
            contentData as unknown as TooltipDatum,
            setTooltipCoords,
            fillColor,
          );
        } else {
          dispatchRef.current.showTooltip(
            contentData as unknown as TooltipDatum,
            undefined,
            fillColor,
          );
        }
      } else {
        dispatchRef.current.clearFixed();
      }
    },
    [dispatchRef, setHighlightedItem],
  );

  return { onHover };
};
